import { useEffect, useState } from 'react';
import { CMSContent } from 'types/cmsType';
import { getCodeFromLang } from 'utils/helpers';
import { SiteGlossaryRoute } from 'routes/route';

import useCMS from './useCMS';
import useLang from './useLang';

const useCreateLink = () => {
  const { fetchCMS } = useCMS();
  const { currentLang } = useLang();

  const [siteGlossary, setSiteGlossary] = useState<CMSContent | null>(null);

  const { accordions } = siteGlossary?.detail || {};

  useEffect(() => {
    // Fetch the site-glossary data initially
    fetchCMS('site-glossary', getCodeFromLang(currentLang), false).then((res) => {
      setSiteGlossary(res);
    }).catch(e=>console.log(e));
  }, [currentLang]); // Trigger when language changes

  useEffect(() => {
    // Function to replace keyword with a link
    const replaceKeywordWithLink = (keyword: string, link: string): void => {
      const escapeRegExp = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const keywordRegex = new RegExp(`\\b${escapeRegExp(keyword)}\\b(?![;:,.])`, 'i');
      //const keywordRegex = new RegExp(`\\b${keyword}\\b`, 'i');

      // Define the XPath expression to locate nodes containing the keyword in their text content.
      // `//*`: Selects all elements in the document, regardless of their tag name.
      // `[text()[contains(., '${keyword}')]]`: Filters elements to include only those
      //   where their direct text content contains the specified keyword.
      // `contains(., '${keyword}')`: Checks if the text content (represented by `.`) of the node contains the keyword.
      const xpath = `//*[normalize-space(text())='${keyword}']`;
      // const xpath = `//*[text()[contains(., '${keyword}')]]`;

      // Execute the XPath query to find elements containing the keyword in their text.
      // `xpath`: The XPath query string to locate nodes with matching text content.
      // `document`: The context within which to execute the query (the entire document in this case).
      // `null`: The namespace resolver (not needed here, so it is null).
      // `XPathResult.UNORDERED_NODE_SNAPSHOT_TYPE`: Specifies the type of result to return.
      //   This option collects all matching nodes in an unordered list (snapshot).
      // `null`: Indicates that no pre-existing `XPathResult` is being reused.
      const elements = document.evaluate(xpath, document, null, XPathResult.UNORDERED_NODE_SNAPSHOT_TYPE, null);

      for (let i = 0; i < elements.snapshotLength; i++) {
        // Get the current element from the snapshot
        const node = elements.snapshotItem(i) as HTMLElement | null;

        // Check if the node is valid, does not already contain a link (`<a>` tag),
        // and is not itself a link (`<a>` tag)

        if (node && !node.querySelector('a') && node.nodeName.toLowerCase() !== 'a') {
          // Get the text content of the node (fall back to an empty string if null)
          const text = node.textContent || '';

          // Check if the text matches the keyword regex
          if (keywordRegex.test(text)) {
            // Replace the keyword in the text with a hyperlink using the `link` provided
            const updatedHTML = text.replace(
              keywordRegex,
              `<a href="${link}" target="_blank" rel="noopener noreferrer">${keyword}</a>`
            );

            // Create a temporary `<div>` to hold the updated HTML
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = updatedHTML;

            // Clear the node's current content
            while (node.firstChild) {
              node.removeChild(node.firstChild);
            }

            // Append the updated content to the node
            Array.from(tempDiv.childNodes).forEach((child) => {
              node.appendChild(child);
            });
          }
        }
      }
    };

    const processLinks = () => {
      // Loop through the accordions and replace their titles with links
      accordions?.forEach((accordion) => {
        const formattedTitle = accordion.title.toLowerCase();
        // .replace(/[^a-z0-9]+/g, '-')
        // .replace(/(^-|-$)/g, ''); // Format the title for the URL
        const link = `/${currentLang}${SiteGlossaryRoute}#${encodeURIComponent(formattedTitle)}`;

        replaceKeywordWithLink(accordion.title, link);
      });
    };

    // Run the function initially
    processLinks();

    // Set up a MutationObserver to watch for DOM changes
    const observer = new MutationObserver(() => {
      processLinks();
    });

    observer.observe(document.body, {
      childList: true, // Watches for added or removed child nodes
      subtree: true, // Watches the entire subtree of the target node
    });

    // Clean up the observer on component unmount
    return () => {
      observer.disconnect();
    };
  }, [accordions, currentLang]);

  return null;
};

export default useCreateLink;
